import { useEffect, useState } from "react";
import { X, Paperclip, Download, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import { workforceApi, formatDays } from "./workforceApi";

/**
 * EventDialog — create OR edit a workforce_event.
 *
 * Half-day model rules (mirror the backend):
 *   • start_half + single day → 0.5 days
 *   • start_half OR end_half + multi day → whole span minus 0.5 per flag
 *
 * Attachments are only enabled AFTER a save so we have an event_id to
 * upload to. Multiple attachments per event are supported.
 */
export default function EventDialog({
  open,
  onClose,
  onSaved,
  employees,
  eventTypes,
  editing, // full event object or null
  readOnly,
  defaultStartDate,   // ISO YYYY-MM-DD, used only when creating a new event
}) {
  const [form, setForm] = useState(defaultForm(editing, defaultStartDate));
  const [saving, setSaving] = useState(false);
  const [attachments, setAttachments] = useState(editing?.attachments || []);
  const [uploading, setUploading] = useState(false);
  const eventId = editing?.id;

  useEffect(() => {
    setForm(defaultForm(editing, defaultStartDate));
    setAttachments(editing?.attachments || []);
  }, [editing, open, defaultStartDate]);

  if (!open) return null;

  const enabledTypes = eventTypes.filter((t) => t.enabled);
  const duration = computeDuration(form.start_date, form.end_date, form.start_half, form.end_half);

  const save = async () => {
    if (!form.employee_id) return toast.error("Select an employee");
    if (!form.event_type_code) return toast.error("Select an event type");
    if (!form.start_date || !form.end_date) return toast.error("Set start and end dates");
    if (form.end_date < form.start_date) return toast.error("End date must be on or after start date");
    setSaving(true);
    try {
      const body = { ...form };
      if (eventId) {
        await workforceApi.updateEvent(eventId, body);
        toast.success("Event updated");
      } else {
        await workforceApi.createEvent(body);
        toast.success("Event created");
      }
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const upload = async (file) => {
    if (!eventId) return toast.error("Save the event first, then attach files.");
    setUploading(true);
    try {
      const r = await workforceApi.uploadAttach(eventId, file);
      setAttachments((prev) => [...prev, r.data]);
      toast.success("Attachment added");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const removeAttach = async (aid) => {
    try {
      await workforceApi.removeAttach(eventId, aid);
      setAttachments((prev) => prev.filter((a) => a.id !== aid));
      toast.success("Attachment removed");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Remove failed");
    }
  };

  const setStatus = async (status) => {
    if (!eventId) return;
    try {
      await workforceApi.updateEvent(eventId, { status });
      toast.success(`Marked ${status}`);
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Status change failed");
    }
  };

  return (
    <div
      data-testid="workforce-event-dialog"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="flex max-h-[90vh] w-full max-w-2xl flex-col rounded-lg border border-slate-200 bg-white shadow-2xl"
      >
        <div className="flex items-start justify-between gap-3 border-b border-slate-200 px-5 py-4">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
              Workforce Event
            </div>
            <div className="mt-0.5 font-display text-lg font-semibold text-slate-900">
              {editing ? "Edit Event" : "New Event"}
            </div>
          </div>
          <button
            data-testid="workforce-event-close"
            onClick={onClose}
            className="rounded-md p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-900"
          >
            <X size={18} weight="bold" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <Field label="Employee">
              <select
                data-testid="workforce-event-employee"
                disabled={readOnly}
                value={form.employee_id}
                onChange={(e) => setForm({ ...form, employee_id: e.target.value })}
                className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
              >
                <option value="">Select employee…</option>
                {employees
                  .filter((e) => e.status === "Active")
                  .map((e) => (
                    <option key={e.id} value={e.id}>{e.full_name}</option>
                  ))}
              </select>
            </Field>
            <Field label="Event Type">
              <select
                data-testid="workforce-event-type"
                disabled={readOnly}
                value={form.event_type_code}
                onChange={(e) => setForm({ ...form, event_type_code: e.target.value })}
                className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
              >
                <option value="">Select type…</option>
                {enabledTypes.map((t) => (
                  <option key={t.code} value={t.code}>{t.name}</option>
                ))}
              </select>
            </Field>
            <Field label="Start Date">
              <input
                data-testid="workforce-event-start"
                type="date"
                disabled={readOnly}
                value={form.start_date}
                onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
              />
              <label className="mt-1 flex items-center gap-2 text-xs text-slate-600">
                <input
                  type="checkbox"
                  data-testid="workforce-event-start-half"
                  disabled={readOnly}
                  checked={form.start_half}
                  onChange={(e) => setForm({ ...form, start_half: e.target.checked })}
                />
                Half day (start)
              </label>
            </Field>
            <Field label="End Date">
              <input
                data-testid="workforce-event-end"
                type="date"
                disabled={readOnly}
                value={form.end_date}
                onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
              />
              <label className="mt-1 flex items-center gap-2 text-xs text-slate-600">
                <input
                  type="checkbox"
                  data-testid="workforce-event-end-half"
                  disabled={readOnly}
                  checked={form.end_half}
                  onChange={(e) => setForm({ ...form, end_half: e.target.checked })}
                />
                Half day (end)
              </label>
            </Field>
            <Field label="Status">
              <select
                data-testid="workforce-event-status"
                disabled={readOnly}
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
                className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
              >
                <option value="Pending">Pending</option>
                <option value="Approved">Approved</option>
                <option value="Declined">Declined</option>
              </select>
            </Field>
            <Field label="Duration">
              <div
                data-testid="workforce-event-duration"
                className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-mono text-slate-900"
              >
                {formatDays(duration)} day{duration === 1 ? "" : "s"}
              </div>
            </Field>
          </div>

          <Field label="Notes">
            <textarea
              data-testid="workforce-event-notes"
              disabled={readOnly}
              rows={3}
              value={form.notes || ""}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
              placeholder="Optional — reason, coverage plan, etc."
            />
          </Field>

          {/* Attachments — only after save so we have an event_id */}
          <div>
            <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
              Attachments {editing ? `(${attachments.length})` : ""}
            </div>
            {!eventId && (
              <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
                Save the event first to attach the signed paper form, doctor&apos;s note, or supporting documents.
              </div>
            )}
            {eventId && (
              <div className="space-y-2">
                {attachments.map((a) => (
                  <div
                    key={a.id}
                    data-testid={`workforce-attach-${a.id}`}
                    className="flex items-center justify-between rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <Paperclip size={14} className="text-slate-500 shrink-0" />
                      <span className="truncate">{a.filename}</span>
                      <span className="text-xs text-slate-500 shrink-0">
                        {(a.size / 1024).toFixed(0)} KB
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <a
                        href={`${process.env.REACT_APP_BACKEND_URL}/api/workforce/events/${eventId}/attachments/${a.id}/download`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="rounded p-1 text-slate-500 hover:bg-slate-100"
                        title="Download"
                      >
                        <Download size={14} />
                      </a>
                      {!readOnly && (
                        <button
                          onClick={() => removeAttach(a.id)}
                          className="rounded p-1 text-rose-500 hover:bg-rose-50"
                          title="Remove"
                        >
                          <Trash size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
                {!readOnly && (
                  <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50">
                    <Paperclip size={12} weight="bold" />
                    {uploading ? "Uploading…" : "Attach file"}
                    <input
                      type="file"
                      data-testid="workforce-event-upload"
                      className="hidden"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) upload(f);
                        e.target.value = "";
                      }}
                    />
                  </label>
                )}
              </div>
            )}
          </div>
        </div>

        {!readOnly && (
          <div className="flex items-center justify-between gap-2 border-t border-slate-200 bg-slate-50 px-5 py-3">
            <div className="flex gap-2">
              {eventId && form.status === "Pending" && (
                <>
                  <button
                    data-testid="workforce-event-approve"
                    onClick={() => setStatus("Approved")}
                    className="rounded-md border border-emerald-600 bg-emerald-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-emerald-700"
                  >
                    Approve
                  </button>
                  <button
                    data-testid="workforce-event-decline"
                    onClick={() => setStatus("Declined")}
                    className="rounded-md border border-rose-500 bg-white px-3 py-1.5 text-sm font-semibold text-rose-700 hover:bg-rose-50"
                  >
                    Decline
                  </button>
                </>
              )}
            </div>
            <div className="flex gap-2">
              <button
                onClick={onClose}
                className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                data-testid="workforce-event-save"
                onClick={save}
                disabled={saving}
                className="rounded-md border border-slate-800 bg-charcoal px-3 py-1.5 text-sm font-semibold text-ink-inverse hover:bg-slate-900 disabled:opacity-60"
              >
                {saving ? "Saving…" : editing ? "Save Changes" : "Create Event"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
        {label}
      </div>
      {children}
    </label>
  );
}

function defaultForm(editing, defaultStartDate) {
  if (editing) {
    return {
      employee_id: editing.employee_id,
      event_type_code: editing.event_type_code,
      start_date: editing.start_date,
      end_date: editing.end_date,
      start_half: !!editing.start_half,
      end_half: !!editing.end_half,
      status: editing.status,
      notes: editing.notes || "",
    };
  }
  const seed = defaultStartDate || new Date().toISOString().slice(0, 10);
  return {
    employee_id: "",
    event_type_code: "vacation",
    start_date: seed,
    end_date: seed,
    start_half: false,
    end_half: false,
    status: "Pending",
    notes: "",
  };
}

function computeDuration(start, end, sh, eh) {
  if (!start || !end || end < start) return 0;
  if (start === end) return (sh || eh) ? 0.5 : 1;
  const ms = new Date(end + "T00:00:00") - new Date(start + "T00:00:00");
  let days = Math.round(ms / 86400000) + 1;
  if (sh) days -= 0.5;
  if (eh) days -= 0.5;
  return Math.max(0, days);
}
